import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { CheckCircle2, Loader2, TestTube, XCircle } from "lucide-react";
import type React from "react";
import { useState } from "react";
import type { FieldValues } from "react-hook-form";
import { useTestServer } from "@/app/servers/api/hooks";
import type { FieldPropsWithType } from "@/app/servers/types";

type UriFieldProps = FieldPropsWithType & {
  isManaged?: boolean;
};

export const UriField = ({
  form,
  canUpdate,
  type,
  isManaged = false,
}: UriFieldProps) => {
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message?: string;
  } | null>(null);
  const { mutateAsync, isPending } = useTestServer();

  const testConnection = async (value: string) => {
    if (!(value && canUpdate)) {
      return;
    }

    setTestResult(null);

    try {
      const response = await mutateAsync({
        type,
        [type]: {
          uri: value.trim(),
        },
      });
      setTestResult({ success: response.success, message: response.message });
    } catch (_error) {
      setTestResult({
        success: false,
        message: "Couldn't reach the server. Is the address correct?",
      });
    }
  };

  if (isManaged) {
    return (
      <FormItem>
        <FormLabel>URI</FormLabel>
        <FormControl>
          <Input disabled type="text" value="Hosted by Arcade" />
        </FormControl>
        <FormDescription>
          This server is managed and hosted by Arcade
        </FormDescription>
      </FormItem>
    );
  }

  return (
    <FormField
      control={form.control}
      name={`${type}.uri`}
      render={({ field }: { field: FieldValues }) => (
        <FormItem>
          <FormLabel>URI</FormLabel>
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                className={cn(
                  "pr-16",
                  testResult?.success && "border-green-500",
                  testResult?.success === false && "border-red-500"
                )}
                disabled={!canUpdate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e);
                  // Clear test result when input changes
                  setTestResult(null);
                }}
                placeholder="http://localhost:8002"
                type="url"
              />
              <div className="absolute top-1/2 right-3 flex -translate-y-1/2 items-center gap-2">
                {isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <>
                    {testResult?.success === true && (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    )}
                    {testResult?.success === false && (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    {!!field.value && (
                      <TooltipProvider delay={200}>
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                className="h-4 w-4 has-[>svg]:p-3 dark:bg-zinc-700"
                                disabled={isPending}
                                onClick={() => testConnection(field.value)}
                                size="sm"
                                type="button"
                                variant="ghost"
                              />
                            }
                          >
                            <TestTube className="size-4" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Test connection to server</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </>
                )}
              </div>
            </div>
          </FormControl>
          <FormDescription>
            {testResult?.success === false ? (
              <span className="text-destructive">{testResult.message}</span>
            ) : (
              "The base URL of the server's tools"
            )}
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
